import classes from "./ReferencesBlock.module.css";
import {
    ActionIcon,
    alpha,
    Box,
    Card,
    Collapse,
    Divider,
    Group,
    List,
    ThemeIcon,
    Title,
    Tooltip,
    Transition,
} from "@mantine/core";
import IconBooks from "../../../../../assets/books.svg?react";
import IconTools from "../../../../../assets/tools.svg?react";
import {
    SourceInfo,
    HasEditorPrivileges,
    RelatedSourceInfo,
} from "../../../../../types";
import {useTranslation} from "react-i18next";
import IconBlockquote from "../../../../../assets/blockquote.svg?react";
import {RoleProtection} from "../../RoleProtection";
import {useDisclosure} from "@mantine/hooks";
import {ReferenceItem} from "../../../sliceChatComponents/ReferenceItem";

// Re-export for backwards compatibility
export {ReferenceItem};

/*
type ReferencesBlockProps = {
    items: ReferenceItem[];
}; */
export const ReferencesBlock = ({
    id,
    items,
    consideredSources,
    relatedTools,
    messageContent,
    isFeedback,
}: {
    id?: number;
    items: SourceInfo[];
    consideredSources: SourceInfo[];
    relatedTools: RelatedSourceInfo[];
    messageContent?: string;
    isFeedback?: boolean;
}) => {
    const {t} = useTranslation();
    const mounted = items.length > 0;
    const [consideredOpen, {toggle}] = useDisclosure(false);

    // Filter relatedTools to only include those referenced in the message
    const filteredRelatedTools = messageContent
        ? relatedTools.filter((tool: RelatedSourceInfo) =>
              messageContent.includes(tool.url)
          )
        : relatedTools;
    const unfilteredRelatedTools = relatedTools.filter(
        (tool: RelatedSourceInfo) => !messageContent.includes(tool.url)
    );
    // Make sure all items are unique by id
    const uniqueIds = Array.from(
        new Set(filteredRelatedTools.map((item) => item.id))
    );
    const uniqueItems = uniqueIds
        .map((id) => filteredRelatedTools.find((item) => item.id === id))
        .filter((item) => item !== undefined);

    return (
        <Transition transition="fade-up" duration={500} mounted={mounted}>
            {(styles) => (
                <Card
                    bg={"var(--dib-light-blue)"}
                    className={classes.card}
                    style={styles}
                >
                    <Box>
                        <Group justify="space-between" align="center">
                            <Group
                                gap="xs"
                                align="flex-start"
                                wrap="nowrap"
                                className={classes.cardHeader}
                            >
                                <ThemeIcon
                                    color="var(--dib-darkest-of-greens)"
                                    variant="subtle"
                                    w="var(--book-icon-size)"
                                    h="var(--book-icon-size)"
                                >
                                    <IconBooks />
                                </ThemeIcon>
                                <Title order={3} className={classes.cardTitle}>
                                    {t("references.title")}
                                </Title>
                            </Group>
                            <RoleProtection allowedRoles={HasEditorPrivileges}>
                                <Tooltip
                                    label={t(
                                        "references.consideredSources.tooltip"
                                    )}
                                >
                                    <ActionIcon
                                        className={
                                            classes.consideredSourcesButton
                                        }
                                        variant="subtle"
                                        radius={"xl"}
                                        size="sm"
                                        onClick={toggle}
                                    >
                                        <IconBlockquote />
                                    </ActionIcon>
                                </Tooltip>
                            </RoleProtection>
                        </Group>
                        <List
                            className={classes.ol}
                            ff="text"
                            listStyleType="none"
                            spacing={"sm"}
                            styles={{
                                itemWrapper: {
                                    display: "flex",
                                    width: "100%",
                                    alignItems: "stretch",
                                },
                            }}
                        >
                            {items.map((source: SourceInfo) => {
                                return (
                                    <ReferenceItem
                                        index={source.index + 1}
                                        key={source.url}
                                        id={source.id}
                                        desc={source.description}
                                        type={source.type}
                                        href={source.url}
                                        content={
                                            source.segmentName
                                                ? `${source.name} | ${source.segmentName}`
                                                : source.name
                                        }
                                    />
                                );
                            })}
                        </List>
                        {uniqueItems?.length > 0 && (
                            <>
                                <Group
                                    gap="xs"
                                    align="flex-start"
                                    wrap="nowrap"
                                    className={classes.cardHeader}
                                >
                                    <ThemeIcon
                                        color="var(--dib-darkest-of-greens)"
                                        variant="subtle"
                                        w="var(--book-icon-size)"
                                        h="var(--book-icon-size)"
                                    >
                                        <IconTools />
                                    </ThemeIcon>
                                    <Title
                                        order={3}
                                        className={classes.cardTitle}
                                    >
                                        {t("references.relatedTools.title")}
                                    </Title>
                                </Group>
                                <List
                                    className={classes.ol}
                                    ff="text"
                                    listStyleType="none"
                                    spacing={"sm"}
                                    styles={{
                                        itemWrapper: {
                                            display: "flex",
                                            width: "100%",
                                            alignItems: "stretch",
                                        },
                                    }}
                                >
                                    {uniqueItems.map(
                                        (source: RelatedSourceInfo) => {
                                            return (
                                                <ReferenceItem
                                                    key={source.id}
                                                    id={source.id}
                                                    href={source.url}
                                                    content={source.title}
                                                    desc={source.description}
                                                    isTool
                                                />
                                            );
                                        }
                                    )}
                                    {isFeedback &&
                                        unfilteredRelatedTools.map(
                                            (source: RelatedSourceInfo) => {
                                                return (
                                                    <ReferenceItem
                                                        key={source.id}
                                                        id={source.id}
                                                        href={source.url}
                                                        content={source.title}
                                                        desc={
                                                            source.description
                                                        }
                                                        isTool
                                                        isFeedback
                                                    />
                                                );
                                            }
                                        )}
                                </List>
                            </>
                        )}
                        <Collapse in={consideredOpen}>
                            <Divider
                                color={alpha("var(--dib-dark-green)", 0.2)}
                            />
                            <Group
                                gap="xs"
                                align="flex-start"
                                wrap="nowrap"
                                className={classes.cardHeader}
                            >
                                <Title
                                    style={{
                                        paddingLeft:
                                            "calc(var(--book-icon-size) + var(--mantine-spacing-md))",
                                    }}
                                    order={4}
                                    fz={"md"}
                                    className={classes.cardTitle}
                                >
                                    {t("references.consideredSources.title")}
                                </Title>
                            </Group>
                            <List
                                className={classes.ol}
                                ff="text"
                                listStyleType="none"
                                spacing={"sm"}
                                styles={{
                                    itemWrapper: {
                                        display: "flex",
                                        width: "100%",
                                        alignItems: "stretch",
                                    },
                                }}
                            >
                                {consideredSources.map((source: SourceInfo) => {
                                    return (
                                        <ReferenceItem
                                            index={source.index + 1}
                                            key={source.id}
                                            id={source.id}
                                            desc={source.description}
                                            type={source.type}
                                            href={source.url}
                                            content={
                                                source.segmentName
                                                    ? `${source.name} | ${source.segmentName}`
                                                    : source.name
                                            }
                                        />
                                    );
                                })}
                            </List>
                        </Collapse>
                    </Box>
                </Card>
            )}
        </Transition>
    );
};
